import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ExternalLink, LifeBuoy, Users } from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { Reveal, SectionLabel, MaskLines } from "@/components/common/Reveal";
import { StoreButtons } from "@/components/common/StoreButtons";
import { HealthDisclaimer } from "@/components/common/HealthDisclaimer";
import { getApp } from "@/data/apps";
import { sources } from "@/data/site";

const LinkPill = ({ to, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-gray-300 transition-colors duration-300 hover:border-[var(--accent)] hover:text-white"
    style={{ border: "1px solid var(--line)" }}
  >
    {children}
  </Link>
);

export default function AppDetail() {
  const { slug } = useParams();
  const app = getApp(slug);

  if (!app) {
    return (
      <div className="shell pb-32 pt-40" data-testid="app-not-found">
        <h1 className="font-display text-3xl font-black text-white">App not found</h1>
        <p className="mt-4 text-sm text-gray-400">
          This app is not published (or the link has changed).
        </p>
        <Link to="/apps" className="link-underline mt-8 inline-block text-sm text-white">
          Back to all apps
        </Link>
      </div>
    );
  }

  const soon = app.status === "coming-soon";
  const appSources = sources.filter((s) => s.apps?.includes(app.id));

  return (
    <>
      <Seo
        title={app.seo?.title || app.name}
        description={app.seo?.description || app.shortDescription}
        image={app.icon}
        path={`/apps/${app.slug}`}
      />

      <section className="grain relative overflow-hidden pt-32 sm:pt-40">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-56 right-0 h-[600px] w-[820px] blur-3xl"
          style={{ background: `radial-gradient(50% 50% at 60% 40%, ${app.accent}33, transparent)` }}
        />
        <div className="shell relative">
          <Link
            to="/apps"
            data-testid="app-detail-back"
            className="link-underline inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-gray-500"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All apps
          </Link>

          <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end">
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              src={app.icon}
              alt={`${app.name} app icon`}
              className="h-24 w-24 rounded-3xl object-cover sm:h-28 sm:w-28"
              style={{ border: "1px solid var(--line)" }}
            />
            <div>
              <span
                className="rounded-full px-3 py-1 font-mono-x text-[10px] uppercase tracking-[0.16em]"
                style={{
                  color: soon ? "#9CA3AF" : app.accent,
                  border: `1px solid ${soon ? "var(--line)" : app.accent + "66"}`,
                }}
                data-testid={`app-detail-status-${app.slug}`}
              >
                {soon ? "Coming soon" : "Available"} · {app.category}
              </span>
              <h1 className="font-display mt-5 text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                <MaskLines lines={[app.name]} delay={0.1} />
              </h1>
            </div>
          </div>

          <Reveal delay={0.2}>
            <p className="font-display mt-10 max-w-2xl text-xl font-bold leading-snug text-white/90 sm:text-2xl">
              {app.tagline}
            </p>
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-gray-400 sm:text-base">
              {app.fullDescription}
            </p>
          </Reveal>

          <Reveal delay={0.26}>
            <div className="mt-10">
              <StoreButtons app={app} size="lg" />
              <p className="mt-4 flex flex-wrap gap-2 font-mono-x text-[11px] uppercase tracking-[0.16em] text-gray-500">
                <span>Platforms:</span>
                <span className="text-gray-300">{app.platforms.join(" · ")}</span>
                <span className="text-gray-600">|</span>
                <span>Release: {app.releaseDate}</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {app.screenshots.length > 0 && (
        <section className="py-20 sm:py-28" data-testid="app-detail-screenshots">
          <div className="shell">
            <SectionLabel>Screenshots</SectionLabel>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {app.screenshots.map((s, i) => (
                <motion.figure
                  key={s.url}
                  initial={{ opacity: 0, y: 44 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden rounded-3xl"
                  style={{ border: "1px solid var(--line)", background: "var(--surface)" }}
                >
                  <img
                    src={s.url}
                    alt={s.caption}
                    loading="lazy"
                    className="block aspect-[9/17] w-full object-cover object-top"
                  />
                  <figcaption className="px-5 py-4 text-xs text-gray-500">{s.caption}</figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section
        className="grain py-20 sm:py-28"
        style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)" }}
        data-testid="app-detail-features"
      >
        <div className="shell">
          <SectionLabel>Key features</SectionLabel>
          <div className="mt-12 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {app.features.map((f, i) => (
              <Reveal key={f.title + i} delay={i * 0.05}>
                <div style={{ borderTop: "1px solid var(--line)" }} className="pt-6">
                  <h3 className="font-display text-lg font-bold text-white">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="shell grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionLabel>Who it is for</SectionLabel>
            <ul className="mt-8 space-y-4" data-testid="app-detail-audience">
              {app.audience.map((a) => (
                <li key={a} className="flex items-start gap-3 text-sm text-gray-300 sm:text-base">
                  <Users
                    className="mt-1 h-4 w-4 shrink-0"
                    style={{ color: "var(--accent)" }}
                    aria-hidden
                  />
                  {a}
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="app-detail-pricing">
            <SectionLabel>Free &amp; premium</SectionLabel>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                { t: "Free", items: app.pricing.free },
                { t: "Premium", items: app.pricing.premium },
              ].map((tier) => (
                <div
                  key={tier.t}
                  className="rounded-3xl p-6"
                  style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
                >
                  <p className="label">{tier.t}</p>
                  <ul className="mt-5 space-y-2.5">
                    {tier.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check
                          className="mt-0.5 h-3.5 w-3.5 shrink-0"
                          style={{ color: "var(--accent)" }}
                          aria-hidden
                        />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-gray-500">{app.pricing.note}</p>
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="shell space-y-8">
          <HealthDisclaimer
            text={app.disclaimer}
            title={app.isHealthApp ? "Health & medical disclaimer" : "Please note"}
            testId={`app-detail-disclaimer-${app.slug}`}
          />

          <div
            className="flex flex-col gap-6 rounded-3xl p-6 sm:p-8"
            style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
          >
            <div className="flex items-start gap-3">
              <LifeBuoy
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: "var(--accent)" }}
                aria-hidden
              />
              <div>
                <h2 className="font-display text-lg font-bold text-white">
                  Support &amp; legal for {app.name}
                </h2>
                <p className="mt-1 text-sm text-gray-400">
                  {app.supportEmail ? (
                    <>
                      Email{" "}
                      <a
                        href={`mailto:${app.supportEmail}`}
                        data-testid={`app-detail-support-email-${app.slug}`}
                        className="link-underline"
                        style={{ color: "var(--accent)" }}
                      >
                        {app.supportEmail}
                      </a>{" "}
                      or use the support form.
                    </>
                  ) : (
                    <>
                      A support address will be published when this app launches. Until then, use
                      the support form.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <LinkPill
                to={`/support?app=${app.slug}`}
                testId={`app-detail-support-link-${app.slug}`}
              >
                <LifeBuoy className="h-3.5 w-3.5" /> Support
              </LinkPill>
              <LinkPill to={app.privacyUrl} testId={`app-detail-privacy-link-${app.slug}`}>
                Privacy Policy
              </LinkPill>
              <LinkPill to={app.termsUrl} testId={`app-detail-terms-link-${app.slug}`}>
                Terms of Use
              </LinkPill>
              {app.sourcesUrl && (
                <LinkPill to={app.sourcesUrl} testId={`app-detail-sources-link-${app.slug}`}>
                  Sources &amp; references
                </LinkPill>
              )}
            </div>
          </div>

          {appSources.length > 0 && (
            <div data-testid={`app-detail-sources-${app.slug}`}>
              <SectionLabel>References used for this app</SectionLabel>
              <ul className="mt-6 space-y-3">
                {appSources.map((s) => (
                  <li
                    key={s.id}
                    className="flex flex-wrap items-center gap-3 py-3 text-sm text-gray-300"
                    style={{ borderTop: "1px solid var(--line)" }}
                  >
                    <span className="text-white">{s.organization}</span>
                    <span className="text-gray-500">— {s.title}</span>
                    {s.url ? (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-underline inline-flex items-center gap-1 text-xs"
                        style={{ color: "var(--accent)" }}
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="font-mono-x text-[11px] text-gray-600">link pending</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
